import { Icon } from "./Icon";
import { useI18n } from "../lib/i18n";
import { daysBetween } from "../lib/catch-up";
import type { PlannedMoment } from "./Today";

/**
 * "Did {child} do yesterday's activities?"
 *
 * Most of a homeschool day happens away from the screen, often from the printed
 * plan, and the tick used to live one page away from where parents land. So
 * almost nothing was ever ticked — and because the curriculum only moves on
 * days with a tick, children sat on the first day of the first term.
 *
 * One question, about one earlier day, answerable in a tap per activity. It
 * never lists more than that day, and "Not this time" puts it away for good.
 */
export function CatchUpStrip({
  learnerName,
  day,
  today,
  moments,
  onToggle,
  onDismiss,
}: {
  learnerName: string;
  /** The earlier plan's date, "YYYY-MM-DD". */
  day: string;
  today: string;
  /** That day's core activities; extras are never asked about. */
  moments: PlannedMoment[];
  onToggle: (itemId: string) => void;
  onDismiss: () => void;
}) {
  const { t, locale } = useI18n();
  if (moments.length === 0) return null;
  const allDone = moments.every((moment) => moment.item.status === "done");
  const [y, m, d] = day.split("-").map(Number);
  const weekday = new Date(y, m - 1, d).toLocaleDateString(locale === "ur" ? "ur-PK" : locale, { weekday: "long" });
  const title = daysBetween(day, today) === 1
    ? t("catchup.titleYesterday", { name: learnerName })
    : t("catchup.titleDay", { name: learnerName, day: weekday });

  return (
    <section className="catch-up glass-card" aria-labelledby="catch-up-title">
      <div className="catch-up-head">
        <div>
          <h2 id="catch-up-title">{title}</h2>
          <p>{allDone ? t("catchup.allDone", { name: learnerName }) : t("catchup.copy", { name: learnerName })}</p>
        </div>
        <button className="text-button" onClick={onDismiss}>
          {allDone ? t("common.close") : t("catchup.dismiss")}
        </button>
      </div>
      <ul className="catch-up-list">
        {moments.map((moment) => {
          const done = moment.item.status === "done";
          return (
            <li key={moment.item.id} className={done ? "done" : undefined}>
              <span className={`icon-tile ${moment.activity.color}`}>
                <Icon name={moment.activity.icon} size={17} />
              </span>
              <span className="catch-up-title">{moment.activity.title}</span>
              <button
                className={done ? "round-check done" : "round-check"}
                onClick={() => onToggle(moment.item.id)}
                aria-pressed={done}
                aria-label={
                  done
                    ? t("today.markNotDone", { title: moment.activity.title })
                    : t("today.markComplete", { title: moment.activity.title })
                }
              >
                <Icon name="check" />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
